import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useLocation } from "react-router-dom";

// Check if a route is active or any nested item is active
const isRouteActive = (
  link: NavLink,
  activeRoute: string,
  collapsed: boolean
): boolean => {
  if (link.href && activeRoute === link.href) return true;
  if (link.href && activeRoute.startsWith(link.href + "/")) return true;
  if (link.items && collapsed) {
    return link.items.some((item) =>
      isRouteActive(item, activeRoute, collapsed)
    );
  }
  return false;
};

// Check if an item has active nested routes (used for auto-expanding parents)
const isNestedRouteActive = (link: NavLink, activeRoute: string): boolean => {
  if (link.items) {
    return link.items.some(
      (item) =>
        item.href === activeRoute ||
        (item.href && activeRoute.startsWith(item.href + "/"))
    );
  }
  return false;
};

export type NavLink = {
  label: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  items?: NavLink[];
  badge?: number;
  /** Section divider label — rendered above this item when set */
  sectionLabel?: string;
};

type NavItemProps = {
  link: NavLink;
  collapsed: boolean;
  onClick: (href: string) => void;
  level?: number;
};

function NavItem({ link, collapsed, onClick, level = 0 }: NavItemProps) {
  const { icon: Icon, label, href, items, badge, sectionLabel } = link;
  const hasChildren = items && items.length > 0;

  const location = useLocation();
  const activeRoute = location.pathname;
  const isActive = isRouteActive(link, activeRoute, collapsed);
  const shouldExpand = isNestedRouteActive(link, activeRoute);
  const [isExpanded, setIsExpanded] = useState(isActive || shouldExpand);

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    } else if (href) {
      onClick(href);
    }
  };

  const handleNestedClick = (nestedHref: string) => {
    onClick(nestedHref);
    setIsExpanded(false);
  };

  const isNested = level > 0;
  const padding = isNested ? "pl-10" : "px-3";

  return (
    <>
      {/* Section divider */}
      {sectionLabel && !collapsed && !isNested && (
        <div className="pt-4 pb-1 px-3 first:pt-0">
          <span className="text-[11px] font-medium uppercase tracking-wider text-[var(--color-text-tertiary)]">
            {sectionLabel}
          </span>
        </div>
      )}
      {sectionLabel && collapsed && !isNested && (
        <hr className="my-2 border-[var(--color-border-default)]" />
      )}

      <button
        onClick={handleClick}
        className={`relative w-full flex items-center gap-2.5 ${padding} py-2 rounded-lg transition-colors group ${
          isActive
            ? "bg-[var(--color-info-50)] text-[var(--color-info-700)] font-medium"
            : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-muted)]"
        } ${collapsed && !isNested ? "justify-center" : "justify-between"}`}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Icon className="w-[18px] h-[18px] shrink-0" />

          <span
            className={`text-sm whitespace-nowrap overflow-hidden transition-all duration-200 ${
              collapsed && !isNested ? "hidden" : "block text-left"
            }`}
          >
            {label}
          </span>
        </div>

        {/* Right side: badge or expand arrow */}
        <div className="flex items-center gap-1 shrink-0">
          {badge !== undefined && badge > 0 && !collapsed && (
            <span className="ml-auto inline-flex items-center justify-center rounded-full bg-[var(--color-danger-500)] text-white text-[11px] font-medium px-1.5 min-w-[18px] h-[18px]">
              {badge > 99 ? "99+" : badge}
            </span>
          )}

          {hasChildren && !collapsed && (
            <ChevronRight
              className={`w-4 h-4 shrink-0 transition-transform duration-200 ${
                isExpanded || shouldExpand ? "rotate-90" : ""
              }`}
            />
          )}
        </div>
      </button>

      {/* Nested items */}
      {hasChildren && (isExpanded || shouldExpand) && !collapsed && (
        <div className="space-y-0.5">
          {items.map((item) => (
            <NavItem
              key={item.label}
              link={item}
              collapsed={collapsed}
              onClick={handleNestedClick}
              level={level + 1}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default NavItem;
